import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FileText, Search, DollarSign, Clock, Users, ChevronRight, TrendingDown, Bot, Sparkles, Building2, Package } from 'lucide-react';
import axios from 'axios';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { formatCurrency, formatDate } from '@/lib/utils';
import { useAuth } from '@/context/AuthContext';
import { VerifiedBadge } from '@/components/VerifiedBadge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BrowseRFBs() {
  const [rfbs, setRfbs] = useState([]);
  const [businessRfbs, setBusinessRfbs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('consumer');
  const [aiSuggestion, setAiSuggestion] = useState({});
  const [aiLoading, setAiLoading] = useState({});
  const { token } = useAuth();

  const getAiSuggestion = async (rfbId) => {
    setAiLoading(prev => ({ ...prev, [rfbId]: true }));
    try {
      const res = await axios.post(`${API}/ai/bid-suggestion`, 
        { loan_request_id: rfbId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAiSuggestion(prev => ({ ...prev, [rfbId]: res.data }));
    } catch (err) {
      console.error('AI suggestion failed:', err);
    } finally {
      setAiLoading(prev => ({ ...prev, [rfbId]: false }));
    }
  };

  useEffect(() => {
    const fetchRFBs = async () => {
      try {
        const [consumerRes, businessRes] = await Promise.all([
          axios.get(`${API}/loans/requests?status=pending`),
          axios.get(`${API}/business/loan-requests`, {
            headers: { Authorization: `Bearer ${token}` }
          }).catch(() => ({ data: [] }))
        ]);
        setRfbs(consumerRes.data);
        setBusinessRfbs(businessRes.data);
      } catch (err) {
        console.error('Failed to fetch RFBs:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchRFBs();
  }, [token]);

  const filteredRFBs = rfbs.filter(r => 
    r.purpose.toLowerCase().includes(search.toLowerCase()) ||
    r.amount.toString().includes(search) ||
    r.consumer_name.toLowerCase().includes(search.toLowerCase())
  );

  const filteredBusinessRFBs = businessRfbs.filter(r =>
    r.purpose?.toLowerCase().includes(search.toLowerCase()) ||
    r.amount?.toString().includes(search) ||
    r.business_name?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <DashboardLayout title="Browse Loan Requests">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Browse Loan Requests">
      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <Button variant={tab === 'consumer' ? 'default' : 'outline'} size="sm"
          onClick={() => setTab('consumer')} data-testid="tab-consumer-loans">
          <Users className="w-4 h-4 mr-2" /> Consumer Loans ({rfbs.length})
        </Button>
        <Button variant={tab === 'business' ? 'default' : 'outline'} size="sm"
          onClick={() => setTab('business')} data-testid="tab-business-loans">
          <Building2 className="w-4 h-4 mr-2" /> Business Loans ({businessRfbs.length})
        </Button>
      </div>

      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            data-testid="search-rfbs-input"
            placeholder="Search by purpose, amount, or borrower..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-white/5 border-white/10"
          />
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span className="px-3 py-1 rounded-full bg-primary/10 text-primary">
            {rfbs.length} available
          </span>
        </div>
      </div>

      {/* Consumer Loans */}
      {tab === 'consumer' && (
      <>
      {filteredRFBs.length === 0 ? (
        <Card className="glass border-white/10">
          <CardContent className="py-12 text-center">
            <FileText className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="text-xl font-semibold mb-2">No Loan Requests Available</h3>
            <p className="text-muted-foreground">
              {search ? 'No requests match your search criteria.' : 'Check back later for new lending opportunities.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {filteredRFBs.map((rfb, i) => (
            <motion.div
              key={rfb.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
            >
              <Card className="glass border-white/10 hover:border-primary/30 transition-all h-full">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-2xl font-bold gradient-text">{formatCurrency(rfb.amount)}</h3>
                        <p className="text-muted-foreground">{rfb.purpose}</p>
                      </div>
                      <Badge className="bg-green-500/10 text-green-500">Open</Badge>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="p-3 rounded-lg bg-white/5">
                        <div className="text-xs text-muted-foreground mb-1">Term</div>
                        <div className="font-medium flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {rfb.term_months} months
                        </div>
                      </div>
                      <div className="p-3 rounded-lg bg-white/5">
                        <div className="text-xs text-muted-foreground mb-1">Current Bids</div>
                        <div className="font-medium flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          {rfb.bids_count}
                        </div>
                      </div>
                    </div>

                    {rfb.best_rate && (
                      <div className="flex items-center justify-between p-3 rounded-lg bg-green-500/10 mb-4">
                        <span className="text-sm text-muted-foreground">Best Rate</span>
                        <span className="font-bold text-green-500 flex items-center gap-1">
                          <TrendingDown className="w-4 h-4" />
                          {rfb.best_rate}% APR
                        </span>
                      </div>
                    )}

                    {rfb.collateral_type && rfb.collateral_type !== 'None' && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                        <span className="px-2 py-1 rounded bg-white/5">
                          Collateral: {rfb.collateral_type}
                          {rfb.collateral_amount && ` (${rfb.collateral_amount})`}
                        </span>
                      </div>
                    )}

                    {rfb.preferred_stablecoin && (
                      <div className="flex items-center gap-2 text-sm mb-4">
                        <span className="px-2 py-1 rounded-full bg-blue-500/10 text-blue-400 border border-blue-500/20 text-xs font-medium">
                          Prefers {rfb.preferred_stablecoin}
                        </span>
                      </div>
                    )}

                    <div className="flex items-center justify-between pt-4 border-t border-white/10">
                      <div className="text-sm text-muted-foreground flex items-center gap-2">
                        by {rfb.consumer_name} {rfb.kyc_verified && <VerifiedBadge />} • {formatDate(rfb.created_at)}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          data-testid={`ai-suggest-${rfb.id}`}
                          className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
                          onClick={(e) => { e.preventDefault(); getAiSuggestion(rfb.id); }}
                          disabled={aiLoading[rfb.id]}
                        >
                          {aiLoading[rfb.id] ? (
                            <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin mr-1" />
                          ) : (
                            <Sparkles className="w-4 h-4 mr-1" />
                          )}
                          AI Suggest
                        </Button>
                        <Link to={`/lender/rfbs/${rfb.id}`}>
                          <Button size="sm" className="gradient-primary">
                            Place Bid
                            <ChevronRight className="ml-1 w-4 h-4" />
                          </Button>
                        </Link>
                      </div>
                    </div>

                    {/* AI Suggestion */}
                    {aiSuggestion[rfb.id] && (
                      <motion.div 
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        data-testid={`ai-result-${rfb.id}`}
                        className="mt-4 p-4 rounded-xl bg-blue-500/5 border border-blue-500/20"
                      >
                        <div className="flex items-center gap-2 mb-3">
                          <Bot className="w-4 h-4 text-blue-400" />
                          <span className="text-sm font-semibold text-blue-400">AI Recommendation</span>
                          <span className="text-[10px] bg-blue-500/20 px-2 py-0.5 rounded-full text-blue-300">Gemini 3 Flash</span>
                        </div>
                        <div className="flex items-center gap-4 mb-3">
                          <div className="px-3 py-1.5 rounded-lg bg-blue-500/20">
                            <div className="text-xs text-blue-300">Suggested APR</div>
                            <div className="text-lg font-bold text-blue-400">{aiSuggestion[rfb.id].suggested_apr}%</div>
                          </div>
                          <div className="px-3 py-1.5 rounded-lg bg-white/5">
                            <div className="text-xs text-muted-foreground">Risk</div>
                            <div className={`text-sm font-semibold ${
                              aiSuggestion[rfb.id].confidence === 'Low' ? 'text-green-400' :
                              aiSuggestion[rfb.id].confidence === 'Medium' ? 'text-yellow-400' : 'text-red-400'
                            }`}>{aiSuggestion[rfb.id].confidence}</div>
                          </div>
                          <div className="px-3 py-1.5 rounded-lg bg-white/5">
                            <div className="text-xs text-muted-foreground">Action</div>
                            <div className="text-sm font-semibold">{aiSuggestion[rfb.id].recommended_action}</div>
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground leading-relaxed">
                          {aiSuggestion[rfb.id].reasoning}
                        </p>
                      </motion.div>
                    )}
                  </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}
      </>
      )}

      {/* Business Loans */}
      {tab === 'business' && (
        <>
          {filteredBusinessRFBs.length === 0 ? (
            <Card className="glass border-white/10">
              <CardContent className="py-12 text-center">
                <Building2 className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                <h3 className="text-xl font-semibold mb-2">No Business Loan Requests</h3>
                <p className="text-muted-foreground">
                  {search ? 'No business requests match your search.' : 'Check back later for business lending opportunities.'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {filteredBusinessRFBs.map((rfb, index) => (
                <motion.div key={rfb.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}>
                  <Card className="glass border-white/10 hover:border-primary/30 transition-all">
                    <CardContent className="p-6">
                      <div className="flex flex-col md:flex-row justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <Building2 className="w-5 h-5 text-purple-400" />
                            <h3 className="text-lg font-semibold" data-testid={`business-rfb-name-${rfb.id}`}>
                              {rfb.business_name}
                            </h3>
                            <Badge className="bg-purple-500/10 text-purple-400 border-purple-500/20 capitalize">
                              {rfb.business_size} business
                            </Badge>
                            {rfb.preferred_stablecoin && (
                              <Badge className="bg-blue-500/10 text-blue-400 border-blue-500/20">
                                Prefers {rfb.preferred_stablecoin}
                              </Badge>
                            )}
                            {rfb.kyc_verified && <VerifiedBadge />}
                          </div>

                          <div className="flex items-center gap-6 text-sm text-muted-foreground mb-3">
                            <span className="flex items-center gap-1"><DollarSign className="w-4 h-4" /> {formatCurrency(rfb.amount)}</span>
                            <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {rfb.term_days} days</span>
                            <span>{rfb.purpose}</span>
                            {rfb.industry && <span className="text-xs bg-white/5 px-2 py-0.5 rounded">{rfb.industry}</span>}
                          </div>

                          {/* Collateral Info */}
                          {rfb.collateral && (
                            <div className="flex items-center gap-4 text-sm">
                              <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-green-500/10 text-green-400 border border-green-500/20">
                                <Package className="w-3.5 h-3.5" />
                                Collateral: {formatCurrency(rfb.collateral.total_value)}
                              </span>
                              <span className="text-gray-500">
                                Advance rate: {rfb.collateral.advance_rate}%
                              </span>
                              {rfb.collateral.receivable_count > 0 && (
                                <span className="text-gray-500">{rfb.collateral.receivable_count} invoices</span>
                              )}
                              {rfb.collateral.asset_count > 0 && (
                                <span className="text-gray-500">{rfb.collateral.asset_count} assets</span>
                              )}
                            </div>
                          )}
                        </div>

                        <div className="flex flex-col items-end gap-2">
                          <div className="text-right">
                            <div className="text-lg font-bold">{rfb.bids_count || 0} bids</div>
                            {rfb.best_rate && <div className="text-sm text-green-400">Best: {rfb.best_rate}%</div>}
                          </div>
                          <Link to={`/lender/business-rfb/${rfb.id}`}>
                            <Button size="sm" className="bg-gradient-to-r from-blue-500 to-purple-500"
                              data-testid={`bid-business-${rfb.id}`}>
                              View & Bid <ChevronRight className="w-4 h-4 ml-1" />
                            </Button>
                          </Link>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </>
      )}
    </DashboardLayout>
  );
}
